import React, {useState, useEffect, useContext, useMemo} from 'react'
import {IconButton, Modal, Button} from '@material-ui/core'
import {Close, MoreVert} from '@material-ui/icons'
import moment from 'moment'
import _ from 'lodash'

// custom components
import {Tab, Tabs} from './Tabs'
import TabDetails from './TabDetails'
import TabAdditionalFields from './TabAdditionalFields'
import TabActionFields from './TabActionFields'
import MenuActions from './MenuActions'
import usePop from '../../utils/usePop'

// contexts
import {ReservationConfigurationContext} from '../../context/ReservationConfiguration'

// styles
import './CalendarEventPopup.scss'
import MenuMore from './MenuMore'
import {getReservationItemDetails} from '../../state/get-reservation-item-details/actions'
import useGetReservationItemDetails from '../../state/get-reservation-item-details/reducer'
import LoadingIndicator from '../ReservationConfiguration/LoadingIcon'
import NextStepActionModal from '../ReservationBookings/NextStepActionModal'
import ApprovalButtons from './ApprovalButtons'
import {UserContext} from '../../context/User'
import {putApproveReject} from '../../state/reservation/put-approve-reject/actions'
import usePutApproveRejectReducer from '../../state/reservation/put-approve-reject/reducer'
import {ToastMessageContext} from '../../context/ToastMessage/ToastMessage'
import useDeleteCalendarViewReducer from '../../state/reservation/delete-reservation/reducer'
import {deleteReservation} from '../../state/reservation/delete-reservation/action'
import GlobalEvents from '../../utils/EventSystem'
import {SettingsContext} from '../../context/Settings/Settings'
import useCalendarViewContext from '../../context/CalendarView'
import ReservationReserve from '../ReservationReserve/ReservationReserve'
import {ReservationContext} from '../../context/Reservation'
import {useModal} from '../../common/Modal'
import {useTranslation} from 'react-i18next'
import DownArrow from './Shape.svg'

const CalendarEventPopup = ({onClose, open, data = {}, source = 'gantt'}) => {
  const {t} = useTranslation()
  const settings = useContext(SettingsContext)
  const currentUser = useContext(UserContext)
  const ctxToast = useContext(ToastMessageContext)

  const simpleModal = useModal('simple', {})
  const {selectedViewGroup} = useCalendarViewContext()
  const {groupActions, getGroupActions, getConfiguration, gconfiguration} = React.useContext(
    ReservationConfigurationContext
  )
  const {clearReservation, clearReservationsData, update} = useContext(ReservationContext)
  const [reservation, dispatch] = useGetReservationItemDetails()
  const [approval, approvalDispatch] = usePutApproveRejectReducer()
  // const [deleteDispatch] = useDeleteCalendarViewReducer()
  const [remove, deleteDispatch] = useDeleteCalendarViewReducer()
  const [tabIndex, setTabIndex] = useState(0)
  const [popReserve, setPopReserve] = useState(false)
  const {
    reservation_uid,
    display_name_first,
    display_with_secondary,
    display_name_third,
    entity_object,
    raw,
  } = data
  const {status = '', states = ''} = reservation.data || {}
  const entity_id = reservation.data?.entity_id

  const stateActionsMenu = usePop()
  const stateMoreMenu = usePop()
  const stateNextStepAction = usePop()

  const canAction = gconfiguration?.data?.active || false

  const canCancel = useMemo(() => {
    if (!reservation?.data?.user_id || !selectedViewGroup) return false

    const cancelAll =
      !!settings.data?.user_access_rules?.can_cancel_all?.reservations.find(
        f => f === selectedViewGroup.key
      ) || false

    const cancelOwn =
      !!settings.data?.user_access_rules?.can_cancel_own?.reservations.find(
        f => f === selectedViewGroup.key
      ) || false

    const isOwner = reservation.data.user_id + '' === localStorage?.user_id
    return cancelAll || (cancelOwn && isOwner)
  }, [settings, selectedViewGroup, reservation])

  const canEdit = useMemo(() => {
    if (!reservation?.data?.user_id || !selectedViewGroup) return false

    const cancelAll =
      !!settings.data?.user_access_rules?.can_edit?.reservations.find(
        f => f === selectedViewGroup.key
      ) || false
    return cancelAll
  }, [settings, selectedViewGroup, reservation])

  const canDelete = useMemo(() => {
    if (!reservation?.data?.user_id || !selectedViewGroup) return false

    const deleteAll =
      !!settings.data?.user_access_rules?.can_delete?.reservations.find(
        f => f === selectedViewGroup.key
      ) || false
    return deleteAll
  }, [settings, selectedViewGroup, reservation])

  const handleApproveReject = (event, axn, data) => {
    const params = {
      manager_id: currentUser.details.id,
      entity_object_ids: reservation.data.entity_object_ids,
      action_object_ids: reservation.data.action_object_ids,
      status_action: axn,
      comment: data.message || '',
    }

    putApproveReject(approvalDispatch, params).then(res => {
      // call api
      getReservationItemDetails(dispatch, {
        group_id: reservation.data.entity_id,
        uid: reservation.data.uid,
      })
    })
  }

  const [full_value, new_value] = useMemo(() => {
    let reservationID = ''
    const prepareValue = val => {
      let display_value
      if ((val !== null && val !== undefined && val.length > 0) || typeof val == 'number') {
        display_value = `${val},`
      } else {
        display_value = ''
      }
      return display_value
    }
    if (!_.isEmpty(data?.reservation_uid)) {
      reservationID = `#${data?.reservation_uid},`
    } else if (!_.isEmpty(data?.entity_display_name)) {
      reservationID = data?.entity_display_name
    }
    const first_name = prepareValue(data.display_name_first || entity_object?.display_name)
    const second_name = prepareValue(
      data.display_with_secondary || entity_object?.display_with_secondary
    )
    const third_name = prepareValue(data.display_name_third || entity_object?.display_name_third)
    const full_value = `${first_name} ${second_name} ${third_name}`.replace(/,\s*$/, '')
    const _new_value =
      full_value.trim() !== '' ? full_value : `${reservationID}`.replace(/,\s*$/, '')
    const _full_value =
      full_value.trim() !== '' ? full_value : `${reservationID} ${status} - ${states}`
    return [_full_value, _new_value]
  }, [data, status, states, entity_object])

  const returnAction = useMemo(() => {
    let op = {}
    if (groupActions.data.length && reservation.data?.performed_actions?.length) {
      groupActions.data.forEach(ga => {
        if (ga.id === reservation.data.performed_actions[0].action_id) {
          if (ga.change_return_name) {
            op = {
              name: ga.return_name,
              action: ga,
            }
          } else {
            op = {
              name: `Return ${ga.name}`,
              action: ga,
            }
          }
        }
      })
    }
    return op
  }, [reservation, groupActions])

  const availableActions = useMemo(() => {
    let op = []
    if (groupActions.data.length && reservation.data?.reserve_for) {
      groupActions.data.forEach(ga => {
        if (reservation.data.reserve_for.action_ids?.includes(ga.id.toString())) {
          let allowed = false
          reservation.data.permitted_action_ids.forEach(pa => {
            if (pa.action_id === ga.id && pa.can_perform) {
              allowed = true
            }
          })
          if (allowed) {
            op.push(ga)
          }
        }
      })
    }
    return op
  }, [reservation, groupActions])

  const handleClickActionsMenu = e => {
    stateActionsMenu.pop(
      {},
      {
        anchorEl: e.currentTarget,
      }
    )
  }
  const handleClickMoreMenu = e => {
    stateMoreMenu.pop(
      {},
      {
        anchorEl: e.currentTarget,
      }
    )
  }

  const handleActionClick = (e, action, isReturn = false) => {
    if (stateActionsMenu.props.open) {
      stateActionsMenu.close()
    }
    stateNextStepAction.pop(
      {},
      {
        action: action,
        group_id: reservation.data.entity_id,
        itemSelected: reservation.data,
        object_ids: reservation.data.entity_object_ids,
        uid: reservation.data.uid,
        isReturn: isReturn,
      }
    )
  }

  const handleSavedNextStepAction = () => {
    // call api
    getReservationItemDetails(dispatch, {
      group_id: reservation.data.entity_id,
      uid: reservation.data.uid,
    })
    // refresh gantt
    GlobalEvents.emit('reservation:next-step', data)
  }

  const handleClickEdit = e => {
    if (currentUser.details.reservation_enabled) {
      setPopReserve(true)
      update('fromGantt', false)
      update('isSelection', false)
      update('is_edit', true)
      update('date_from', moment(reservation.data.start_date))
      update('date_to', moment(reservation.data.end_date))
      update(
        'selected_items',
        reservation.data.entity_object_ids.map((i, o) => {
          return {
            id: i,
            name: `${reservation.data.items[o].primary ? reservation.data.items[o].primary : '-'}
            | ${reservation.data.items[o].secondary ? reservation.data.items[o].secondary : '-'}`,
          }
        })
      )
      update(
        'original_items',
        reservation.data.entity_object_ids.map((i, o) => {
          return {id: i}
        })
      )
      update('reserve_for_field', {})
      update('comments', reservation.data.comments)
      update('selected_group', {id: reservation.data.entity_id})
      update('reserved_for', {
        name: reservation.data.reserve_for.value,
        id: reservation.data.reserve_for.value_id,
        field_id: reservation.data.reserve_for.field_id,
      })
      update('action_ids', reservation.data.reserve_for.action_ids)
      update('additional_fields', reservation.data.additional_fields)
      update('selected_field', {})
      update('selected_group_id', reservation.data.entity_id)
      update('reservation_notification', reservation.data.reservation_notification || [])
      update('reservation_uid', reservation.data.uid)
      update('deleted_item_ids', [])
      update('status', reservation.data.status)
      update('states', reservation.data.states)
    }
  }

  const handleDelete = event => {
    stateMoreMenu.close()
    const temp_data = {
      group_object_ids: reservation.data.entity_object_ids,
      action_object_ids: reservation.data.action_object_ids,
    }
    const reserved_for = reservation.data.reserve_for.value
    const from = moment(reservation.data.start_date).format('MM/DD/YYYY h:mm A')
    const to = moment(reservation.data.end_date).format('MM/DD/YYYY h:mm A')
    if (simpleModal) {
      simpleModal.show({
        scheme: 'destructive',
        title: t('reservations.confirmations.delete.title', {
          reservation_uid: `#${reservation.data.uid}`,
        }),
        description: t('reservations.confirmations.delete.description', {
          reserved_for: reserved_for,
          reservation_start: from,
          reservation_end: to,
        }),
        buttons: {
          affirmative: {
            label: t('reservations.confirmations.delete.affirmative'),
            click: (d, modal) => {
              deleteReservation(deleteDispatch, temp_data).then(res => {
                GlobalEvents.emit('reservation:delete', data)
                onClose()
                ctxToast.popMessage(res.message, 'success', null)
              })
              modal.hide()
            },
          },
          negative: {
            label: t('reservations.confirmations.delete.negative'),
          },
        },
      })
    }
  }

  const isEditable = useMemo(() => {
    if (status === 'requested' && states === 'no_response') return false
    if (status === 'requested' && states === 'rejected') return false
    if (status === 'upcoming' && states === 'abandoned') return false
    if (status === 'fulfilled' && states === 'returned') return false
    if (status === 'cancelled') return false
    return true
  }, [status, states])

  const handleClose = e => {
    setPopReserve(false)
    clearReservation()
    clearReservationsData()
  }

  const handleReserve = id => {
    setPopReserve(false)
    clearReservation()
  }

  const handleClickCancel = e => {
    stateMoreMenu.close()
    if (simpleModal) {
      const reserved_for = reservation.data.reserve_for.value
      const from = moment(reservation.data.start_date).format('MM/DD/YYYY h:mm A')
      const to = moment(reservation.data.end_date).format('MM/DD/YYYY h:mm A')
      simpleModal.show({
        scheme: 'warning',
        title: t('reservations.confirmations.cancel.title', {
          reservation_uid: `#${reservation_uid}`,
        }),
        description: t('reservations.confirmations.cancel.description', {
          reserved_for: reserved_for,
          reservation_start: from,
          reservation_end: to,
        }),
        buttons: {
          negative: {
            label: t('reservations.confirmations.cancel.negative'),
          },
          affirmative: {
            label: t('reservations.confirmations.cancel.affirmative'),
            click: (d, modal) => {
              const params = {
                manager_id: currentUser.details.id,
                entity_object_ids: reservation?.data?.entity_object_ids,
                action_object_ids: reservation?.data?.action_object_ids,
                status_action: 'cancelled',
                comment: '',
              }
              putApproveReject(approvalDispatch, params)
                .then(res => {
                  GlobalEvents.emit('reservation:cancel', data)
                  onClose()
                  ctxToast.popMessage(
                    `Reservation #${reservation_uid} was cancelled.`,
                    `success`,
                    null
                  )
                })
                .catch(e => {
                  ctxToast.popMessage("Something wen't wrong", 'failed', null)
                })
                .finally(() => {
                  modal.hide()
                })
            },
          },
        },
      })
    }
  }

  // effects
  useEffect(() => {
    if (open) {
      let entity_id = null
      let uid = null
      if (source === 'gantt' && !_.isEmpty(data.raw)) {
        entity_id = data.raw.entity_id
        uid = data.raw.reservation_uid
      } else if (source === 'calendar') {
        entity_id = data.entity_id
        uid = data.reservation_uid
      }

      if (entity_id && uid) {
        getReservationItemDetails(dispatch, {
          group_id: entity_id,
          uid: uid,
        })

        getConfiguration({group_id: entity_id})
      }
    }
  }, [dispatch, getConfiguration, open, data.raw, source, data.entity_id, data.reservation_uid])

  useEffect(() => {
    if (!open) return
    const timeout = setTimeout(() => {
      if (
        !!entity_id &&
        !gconfiguration.isLoading &&
        gconfiguration.data.active &&
        gconfiguration.data.action_steps
      ) {
        let actionsIds = gconfiguration.data.action_steps.map(s => {
          return s.action_id
        })
        //call action objects API
        getGroupActions({group_id: entity_id, params: {action_ids: actionsIds}})
      }
    }, 100)
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [
    open,
    entity_id,
    getGroupActions,
    gconfiguration.isLoading,
    gconfiguration.data.active,
    gconfiguration.data.action_steps,
  ])

  const isApprovalManager = useMemo(() => {
    const ids = gconfiguration.data?.manager_ids || []

    return ids.findIndex(f => f + '' === currentUser.details.id + '') !== -1
  }, [gconfiguration, currentUser])
  return (
    <Modal open={!!open} onClose={onClose}>
      <>
        <div
          className="c-res-mod c-calendar-event-popup modal-des"
          style={{borderLeft: `8px solid ${data.bgColor ? data.bgColor : data.color}`}}
        >
          <div
            className="c-calendar-event-popup__header"
            // style={{backgroundColor: `${data.bgColor ? data.bgColor : data.color}3f`}}
          >
            <div className="header-status">
              <h4 className="c-calendar-event-popup__header-title tooltip-header-title ap-typography ap-typography--lg ap-typography--light ap-ellipsis">
                {new_value}
                <span className="tooltip-after"></span>
                <span className="tooltip-title-text">{full_value}</span>
              </h4>
              <div
                className="c-calendar-event-popup__header-status ap-typography ap-typography--sm ap-ellipsis"
                style={{textTransform: 'capitalize'}}
              >
                <span className="ap-typography--light">{status}</span> {states && status && '-'}{' '}
                {states}
              </div>
            </div>
            {((canCancel && status !== 'fulfilled') || (isEditable && canEdit)) && (
              <IconButton
                className="c-calendar-event-popup__header-ellipsis ap-icon-button ap-icon-button--square"
                size="small"
                onClick={handleClickMoreMenu}
                hidden={status === 'cancelled'}
              >
                <MoreVert />
              </IconButton>
            )}
            {/* <IconButton
              className="c-calendar-event-popup__header-close ap-icon-button ap-icon-button--square"
              size="small"
              onClick={onClose}
            >
              <Close />
            </IconButton> */}
          </div>

          <div className="c-calendar-event-popup__body">
            <div className="c-calendar-event-popup__tab-wrapper">
              <Tabs>
                <Tab
                  active={tabIndex === 0}
                  onClick={e => setTabIndex(0)}
                  style={{borderColor: `${data.bgColor ? data.bgColor : data.color}`}}
                  className={`tab-count-${
                    (reservation.data?.performed_actions || []).length > 0 ? 3 : 2
                  }`}
                >
                  Details
                </Tab>
                <Tab
                  active={tabIndex === 1}
                  onClick={e => setTabIndex(1)}
                  style={{borderColor: `${data.bgColor ? data.bgColor : data.color}`}}
                  className={`tab-count-${
                    (reservation.data?.performed_actions || []).length > 0 ? 3 : 2
                  }`}
                >
                  Additional Fields
                </Tab>
                {(reservation.data?.performed_actions || []).length > 0 && (
                  <Tab
                    active={tabIndex === 2}
                    onClick={e => setTabIndex(2)}
                    style={{borderColor: `${data.bgColor ? data.bgColor : data.color}`}}
                    className={`tab-count-3`}
                  >
                    Action Fields
                  </Tab>
                )}
              </Tabs>
              {reservation.isLoading && <LoadingIndicator />}
              {!reservation.isLoading && (
                <>
                  <TabDetails active={tabIndex === 0} data={reservation.data} />
                  <TabAdditionalFields active={tabIndex === 1} data={reservation.data} />
                  {(reservation.data?.performed_actions || []).length > 0 && (
                    <TabActionFields
                      active={tabIndex === 2}
                      data={reservation.data}
                      entity_object={entity_object}
                      actions={groupActions}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="c-calendar-event-popup__footer">
            {canAction &&
              status === 'upcoming' &&
              !groupActions.isLoading &&
              availableActions.length === 1 &&
              reservation.data?.can_perform_next_action && (
                <button
                  className="ap-ellipsis ap-button ap-button--sm ap-typography--sm ap-typography--white ap-bg-primary ap-b-primary action-btn"
                  style={{width: 'max-content', maxWidth: '50%'}}
                  onClick={e => handleActionClick(e, availableActions[0])}
                >
                  {availableActions[0].name}
                </button>
              )}
            {canAction &&
              status === 'upcoming' &&
              !groupActions.isLoading &&
              availableActions.length > 1 &&
              reservation.data?.can_perform_next_action && (
                <Button
                  className="ap-button ap-button--sm ap-typography--sm ap-typography--white ap-bg-primary ap-b-primary action-btn"
                  onClick={handleClickActionsMenu}
                  style={{width: '111px'}}
                >
                  Actions <img src={DownArrow} className="action-down-arrow" alt="arrow" />
                </Button>
              )}
            {canAction &&
              status === 'fulfilled' &&
              states !== 'returned' &&
              !!returnAction?.name &&
              !groupActions.isLoading &&
              groupActions.data.length > 0 && (
                <button
                  className="ap-ellipsis ap-button ap-button--sm ap-typography--sm ap-typography--white ap-bg-primary ap-b-primary action-btn"
                  style={{width: 'max-content', maxWidth: '50%'}}
                  onClick={e => handleActionClick(e, returnAction.action, true)}
                >
                  {returnAction.name}
                </button>
              )}
            {canAction && isApprovalManager && states === 'pending' && (
              <ApprovalButtons
                onClickApprove={(e, d) => handleApproveReject(e, 'approved', d)}
                onClickReject={(e, d) => handleApproveReject(e, 'rejected', d)}
              />
            )}
            <Button
              onClick={onClose}
              style={{width: 80, textTransform: 'capitalize'}}
              // className="cancel-btn ap-button ap-button--text font-bold color-303A52 mt--1"
            >
              Close
            </Button>
          </div>
        </div>
        {canAction && status !== 'pending' && availableActions.length > 1 && (
          <MenuActions
            {...stateActionsMenu.props}
            items={availableActions}
            onClickItem={(e, action) => handleActionClick(e, action)}
            onClose={e => stateActionsMenu.close()}
          />
        )}
        <MenuMore
          {...stateMoreMenu.props}
          onClose={e => stateMoreMenu.close()}
          onClickCancel={handleClickCancel}
          onClickEdit={e => handleClickEdit(e)}
          onClickDelete={e => handleDelete(e)}
          canCancel={canCancel}
          isEditable={isEditable}
          canEdit={canEdit}
          canDelete={canDelete}
          status={status}
        />
        <Modal
          style={{position: 'absolute'}}
          open={popReserve}
          onClose={handleClose}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <ReservationReserve
            fromGantt={reservation.fromGantt}
            onClose={handleClose}
            handleReserve={handleReserve}
          />
        </Modal>
        <NextStepActionModal
          setItemSelected={() => {}}
          setItemUpdated={() => {}}
          onClose={e => stateNextStepAction.close()}
          onSaved={handleSavedNextStepAction}
          {...stateNextStepAction.props}
        />
      </>
    </Modal>
  )
}

export default CalendarEventPopup
